// Web-exclusive Jobs screen - Browse Jobs Page with Master-Detail Layout
// Displays job list on left, job details on right (side-by-side)

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Pressable } from 'react-native';
import { useDebounce } from '../../hooks/useDebounce';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
  Search, X, AlertTriangle, ClipboardList, Briefcase, CheckCircle, MapPin, DollarSign,
  Calendar, CalendarDays, Clock, Stethoscope, Target, ChevronDown, Globe, Check, ChevronRight, Heart, Lightbulb,
  Filter,
} from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import { useIsMobile } from '../../hooks/useIsMobile';
import MobileDetailBackBar from '../../components/web/MobileDetailBackBar';
import WebModal from '../../components/web/WebModal';
import Pagination from '../../components/web/Pagination';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getAllJobs, getJob, getJobFacilities, applyJob } from '../../services/job';
import { getOverview } from '../../services/profile';
import { addFavoriteJob, removeFavoriteJob } from '../../reducers/jobs/favoriteJobs';
import { isFullTimeJob } from '../../constants/lov/professions';
import { formatPrice, getJobDisplayCurrency } from '../../utils/currency';
import { COUNTRIES, ENABLED_COUNTRIES } from '../../constants/countries';
import { getProfessionTypes, getProfessions } from '../../services/shared';
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, sortProfessionsGeneralFirst } from '../../utils/professionUtils';

// Import shared components
import {
  JobListCard,
  JobDetailContent,
  getParentTypeCode,
  getQuickRangeDates,
  JOB_TYPE_OPTIONS,
  DATE_POSTED_OPTIONS,
  QUICK_RANGE_OPTIONS,
  professionTypeIcons,
  sharedStyles,
  viewTabsStyles,
  modalStyles,
} from './components';

const JOBS_PER_PAGE = 6;

const JobsScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isDoctor, country: userCountry, currencyPreset } = useSelector((state) => state.user);
  const { favorites } = useSelector((state) => state.favoriteJobs);
  const isMobile = useIsMobile();

  const [searchQuery, setSearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [selectedJobType, setSelectedJobType] = useState(null); // null = "All Jobs"
  const [selectedCountry, setSelectedCountry] = useState(userCountry || 'MY');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [appliedJobDetail, setAppliedJobDetail] = useState(null);
  const [showJobTypeDropdown, setShowJobTypeDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [expandedProfessionTypes, setExpandedProfessionTypes] = useState([]);
  const [expandedSubtypes, setExpandedSubtypes] = useState([]);
  const [professionSearchQuery, setProfessionSearchQuery] = useState('');
  const [showDatePostedDropdown, setShowDatePostedDropdown] = useState(false);
  const [showSalaryDropdown, setShowSalaryDropdown] = useState(false);
  const [selectedDatePosted, setSelectedDatePosted] = useState(null); // '24hours', '3days', etc. or null
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  // Temporary salary values for the dropdown (only applied on "Apply" click)
  const [tempSalaryMin, setTempSalaryMin] = useState('');
  const [tempSalaryMax, setTempSalaryMax] = useState('');
  // Date filter tab state: 'posted' or 'shift'
  const [currentDateTab, setCurrentDateTab] = useState('posted');
  // Shift date values (actual applied values for Locum shift date filtering)
  const [shiftDateFrom, setShiftDateFrom] = useState('');
  const [shiftDateTo, setShiftDateTo] = useState('');
  // Temporary shift date values for the dropdown
  const [tempShiftDateFrom, setTempShiftDateFrom] = useState('');
  const [tempShiftDateTo, setTempShiftDateTo] = useState('');
  // Selected quick range for shift date
  const [selectedQuickRange, setSelectedQuickRange] = useState(null);

  // Active tab state: 'all' for all jobs, 'saved' for saved jobs
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileView, setMobileView] = useState('list'); // mobile only: 'list' | 'detail'
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);

  // Debounced search values for API calls (300ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const debouncedLocationSearchQuery = useDebounce(locationSearchQuery, 300);

  // Country filter options from enabled countries
  const COUNTRY_OPTIONS = ENABLED_COUNTRIES.map(code => ({
    code,
    label: COUNTRIES[code].name,
    flag: COUNTRIES[code].flag,
  }));

  // Fetch profession types
  const { data: professionTypesData, isLoading: professionTypesLoading } = useQuery({
    queryKey: ['professionTypes'],
    queryFn: getProfessionTypes,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch professions based on selected country
  const { data: professionsData, isLoading: professionsLoading } = useQuery({
    queryKey: ['professions', selectedCountry],
    queryFn: () => getProfessions(selectedCountry || 'MY'),
    staleTime: 5 * 60 * 1000,
  });

  // Organize professions by type with 3-level hierarchy:
  // Level 1: Parent types (e.g. "a" = Doctor, "e-x" = Medical Assistant)
  // Level 2: Sub-types (e.g. "a-a" = General Practitioner, "a-b" = Specialist)
  // Level 3: Professions (e.g. "a-a-1" = Addiction Medicine GP)
  const organizedProfessions = useMemo(() => {
    const professionTypes = professionTypesData?.data?.data || [];
    const professions = professionsData?.data?.data || [];

    const hierarchy = {};

    // Separate parent types and sub-types from profession types
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (isTopLevel) {
        hierarchy[code] = {
          ...type,
          subtypes: [],
          professions: [], // direct professions for types without subtypes
        };
      }
    });

    // Add sub-types to their parent types
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (!isTopLevel) {
        const parentCode = getParentTypeCode(code);
        if (hierarchy[parentCode]) {
          hierarchy[parentCode].subtypes.push({
            ...type,
            professions: [],
          });
        }
      }
    });

    // Add professions to their respective sub-types or parent types
    professions.forEach(profession => {
      const typeCode = profession.profession_type_code;
      if (!typeCode || typeof typeCode !== 'string') return;

      const parentCode = getParentTypeCode(typeCode);
      if (!hierarchy[parentCode]) return;

      // Try to place under matching sub-type
      const subType = hierarchy[parentCode].subtypes.find(s => s.code === typeCode);
      if (subType) {
        subType.professions.push(profession);
      } else {
        // No matching sub-type, add directly to parent
        hierarchy[parentCode].professions.push(profession);
      }
    });

    // Sort sub-types and professions alphabetically, then pin each list's
    // "general" entry (e.g. General Practitioner within Doctor > GP) to the top
    Object.values(hierarchy).forEach(type => {
      type.subtypes.sort((a, b) => {
        const nameA = getLocalizedProfessionTypeName(a, selectedCountry);
        const nameB = getLocalizedProfessionTypeName(b, selectedCountry);
        return nameA.localeCompare(nameB);
      });
      type.subtypes.forEach(sub => {
        sub.professions.sort((a, b) => {
          const nameA = getLocalizedProfessionName(a, selectedCountry);
          const nameB = getLocalizedProfessionName(b, selectedCountry);
          return nameA.localeCompare(nameB);
        });
        sub.professions = sortProfessionsGeneralFirst(sub.professions, sub.code, selectedCountry);
      });
      type.professions.sort((a, b) => {
        const nameA = getLocalizedProfessionName(a, selectedCountry);
        const nameB = getLocalizedProfessionName(b, selectedCountry);
        return nameA.localeCompare(nameB);
      });
      type.professions = sortProfessionsGeneralFirst(type.professions, type.code, selectedCountry);
    });

    return hierarchy;
  }, [professionTypesData, professionsData, selectedCountry]);

  // Filter professions based on search query (searches across all 3 levels)
  const filteredProfessions = useMemo(() => {
    if (!professionSearchQuery) return organizedProfessions;

    const filtered = {};
    const lowerSearch = professionSearchQuery.toLowerCase();

    Object.entries(organizedProfessions).forEach(([code, type]) => {
      const typeName = getLocalizedProfessionTypeName(type, selectedCountry);
      const typeMatches = typeName.toLowerCase().includes(lowerSearch);

      if (typeMatches) {
        // Parent type matches — include everything under it
        filtered[code] = { ...type };
        return;
      }

      // Check sub-types and their professions
      const matchingSubtypes = [];
      type.subtypes.forEach(subType => {
        const subTypeName = getLocalizedProfessionTypeName(subType, selectedCountry);
        const subTypeMatches = subTypeName.toLowerCase().includes(lowerSearch);

        if (subTypeMatches) {
          matchingSubtypes.push({ ...subType });
        } else {
          const matchingProfs = subType.professions.filter(p => {
            const profName = getLocalizedProfessionName(p, selectedCountry);
            return profName.toLowerCase().includes(lowerSearch);
          });
          if (matchingProfs.length > 0) {
            matchingSubtypes.push({ ...subType, professions: matchingProfs });
          }
        }
      });

      // Check direct professions (for types without subtypes)
      const matchingDirectProfessions = type.professions.filter(p => {
        const profName = getLocalizedProfessionName(p, selectedCountry);
        return profName.toLowerCase().includes(lowerSearch);
      });

      if (matchingSubtypes.length > 0 || matchingDirectProfessions.length > 0) {
        filtered[code] = {
          ...type,
          subtypes: matchingSubtypes,
          professions: matchingDirectProfessions,
        };
      }
    });

    return filtered;
  }, [organizedProfessions, professionSearchQuery, selectedCountry]);

  // Handle quick range selection for shift dates
  const handleQuickRangeSelect = (range) => {
    setSelectedQuickRange(range);
    const { from, to } = getQuickRangeDates(range);
    setTempShiftDateFrom(from);
    setTempShiftDateTo(to);
  };

  // Build query string for API (using debounced search values)
  const queryString = useMemo(() => {
    const params = [];
    if (debouncedSearchQuery) params.push(`text=${encodeURIComponent(debouncedSearchQuery)}`);
    if (debouncedLocationSearchQuery) params.push(`address_query=${encodeURIComponent(debouncedLocationSearchQuery)}`);
    if (selectedJobType !== null && selectedJobType !== 4) {
      params.push(`job_type=${selectedJobType}`);
    }
    if (selectedJobType === 4) {
      // International: filter by target country via 'international' param,
      // skip 'country' param (that filters by clinic base location, not target)
      if (selectedCountry) params.push(`international=${selectedCountry}`);
    } else {
      if (selectedCountry) params.push(`country=${selectedCountry}`);
    }
    if (selectedProfessions.length > 0) params.push(`profession_ids=${selectedProfessions.join(',')}`);

    // Date Posted filter (Posted Date tab)
    if (selectedDatePosted) {
      const option = DATE_POSTED_OPTIONS.find(opt => opt.value === selectedDatePosted);
      if (option) {
        const createdMin = moment().subtract(option.days, 'days').startOf('day').valueOf();
        const createdMax = moment().endOf('day').valueOf();
        params.push(`created_min=${createdMin}`);
        params.push(`created_max=${createdMax}`);
      }
    }

    // Shift Date filter (Shift Date tab - for Locum jobs)
    // Backend expects min_timestamp/max_timestamp (filters on start_timestamp/end_timestamp columns)
    if (shiftDateFrom || shiftDateTo) {
      if (shiftDateFrom && moment(shiftDateFrom, 'YYYY-MM-DD', true).isValid()) {
        const shiftFromMoment = moment(shiftDateFrom);
        const isToday = shiftFromMoment.isSame(moment(), 'day');
        // If shiftDateFrom is today, use current time; otherwise use start of day
        const shiftMin = isToday ? moment().valueOf() : shiftFromMoment.startOf('day').valueOf();
        params.push(`min_timestamp=${shiftMin}`);
      }
      if (shiftDateTo && moment(shiftDateTo, 'YYYY-MM-DD', true).isValid()) {
        const shiftMax = moment(shiftDateTo).endOf('day').valueOf();
        params.push(`max_timestamp=${shiftMax}`);
      }
    }

    // Salary filter
    if (salaryMin) params.push(`min_price=${salaryMin}`);
    if (salaryMax) params.push(`max_price=${salaryMax}`);
    return params.join('&');
  }, [debouncedSearchQuery, debouncedLocationSearchQuery, selectedJobType, selectedCountry, selectedProfessions, selectedDatePosted, shiftDateFrom, shiftDateTo, salaryMin, salaryMax]);

  const { data: jobsData, isLoading, error } = useQuery({
    queryKey: ['jobs', { query: queryString }],
    queryFn: getAllJobs,
  });
  const jobsRawResult = jobsData?.data?.data || jobsData?.data || [];
  const jobs = Array.isArray(jobsRawResult) ? jobsRawResult : [];

  // Filter jobs based on active tab (all jobs vs saved jobs)
  const displayedJobs = useMemo(() => {
    if (activeTab === 'saved') {
      return jobs.filter(job => {
        if (!favorites.includes(job.uuid)) return false;
        const isExpired = (job.end_timestamp && moment(job.end_timestamp).isBefore(moment())) || job.status === 'expired';
        return !isExpired;
      });
    }
    return jobs;
  }, [jobs, favorites, activeTab]);

  // Compute non-expired saved jobs count for badge
  const savedJobsCount = useMemo(() => {
    return jobs.filter(job => {
      if (!favorites.includes(job.uuid)) return false;
      const isExpired = (job.end_timestamp && moment(job.end_timestamp).isBefore(moment())) || job.status === 'expired';
      return !isExpired;
    }).length;
  }, [jobs, favorites]);

  // Paginate displayed jobs (6 per page)
  const totalPages = Math.ceil(displayedJobs.length / JOBS_PER_PAGE);
  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * JOBS_PER_PAGE;
    return displayedJobs.slice(start, start + JOBS_PER_PAGE);
  }, [displayedJobs, currentPage]);

  // Fetch detailed job data when a job is selected
  const { data: jobDetailQuery, isLoading: detailLoading } = useQuery({
    queryKey: ['job', selectedJob?.uuid],
    queryFn: () => getJob(selectedJob?.uuid),
    enabled: !!selectedJob?.uuid,
  });
  const jobDetail = jobDetailQuery?.data?.data || jobDetailQuery?.data || selectedJob;

  // Fetch job facilities for clinical expertise
  const { data: jobFacilitiesQuery } = useQuery({
    queryKey: ['jobFacilities', selectedJob?.uuid],
    queryFn: () => getJobFacilities(selectedJob?.uuid),
    enabled: !!selectedJob?.uuid,
  });
  const jobFacilityData = jobFacilitiesQuery?.data?.data;

  // Fetch profile for approval check
  const { data: profileQuery } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const profileInfo = profileQuery?.data?.data;

  // Apply mutation
  const applyMutation = useMutation({
    mutationFn: applyJob,
    onSuccess: () => {
      setApplySuccess(true);
      queryClient.invalidateQueries(['job', selectedJob?.uuid]);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (err) => {
      setShowApplyModal(false);
      setTimeout(() => {
        alert('Oops! ' + (err.response?.data?.message || 'Something went wrong'));
      }, 400);
    },
  });

  // Select first job by default when jobs load or when tab changes
  useEffect(() => {
    if (displayedJobs.length > 0) {
      // Check if current selection exists in displayed jobs
      const currentSelectionExists = selectedJob && displayedJobs.find(j => j.uuid === selectedJob.uuid);
      if (!currentSelectionExists) {
        // Select first job if no valid selection
        setSelectedJob(displayedJobs[0]);
      }
    } else {
      // Clear selection when no jobs are displayed
      setSelectedJob(null);
    }
    // Note: selectedJob is intentionally NOT in dependencies to avoid infinite re-render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedJobs, activeTab]);

  // Reset to first page and clear selected job when any filter or tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedJob(null);
    setMobileView('list');
  }, [debouncedSearchQuery, debouncedLocationSearchQuery, selectedJobType, selectedCountry, selectedProfessions, selectedDatePosted, shiftDateFrom, shiftDateTo, salaryMin, salaryMax, activeTab]);

  // Auto-select first job when page changes
  useEffect(() => {
    if (paginatedJobs.length > 0) {
      setSelectedJob(paginatedJobs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on page change to avoid conflicts with tab-based auto-select
  }, [currentPage]);

  // Reset profession selections when country changes (professions are country-specific)
  useEffect(() => {
    setSelectedProfessions([]);
    setExpandedProfessionTypes([]);
    setExpandedSubtypes([]);
    setProfessionSearchQuery('');
  }, [selectedCountry]);

  // Convert selectedProfessions to Set for O(1) lookup performance
  const selectedProfessionsSet = useMemo(() => new Set(selectedProfessions), [selectedProfessions]);

  const handleApply = () => {
    applyMutation.mutate({ uuid: appliedJobDetail?.uuid });
  };

  const toggleFavorite = useCallback(() => {
    if (!selectedJob) return;
    const isCurrentlyFavorited = favorites.includes(selectedJob.uuid);

    if (isCurrentlyFavorited) {
      // When unsaving on Saved tab, auto-select next job before removing
      if (activeTab === 'saved') {
        const currentIndex = displayedJobs.findIndex(j => j.uuid === selectedJob.uuid);
        const nextJob = displayedJobs[currentIndex + 1] || displayedJobs[currentIndex - 1] || null;
        // Set next job immediately, before the favorite is removed
        if (nextJob) {
          setSelectedJob(nextJob);
        } else {
          // No more saved jobs after this one is removed
          setSelectedJob(null);
        }
      }
      dispatch(removeFavoriteJob(selectedJob.uuid));
    } else {
      dispatch(addFavoriteJob(selectedJob.uuid));
    }
  }, [selectedJob, favorites, activeTab, displayedJobs, dispatch]);

  // Toggle profession type expansion (level 1)
  const toggleProfessionType = (typeCode) => {
    setExpandedProfessionTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(c => c !== typeCode)
        : [...prev, typeCode]
    );
  };

  // Toggle sub-type expansion (level 2)
  const toggleSubtype = (subtypeCode) => {
    setExpandedSubtypes(prev =>
      prev.includes(subtypeCode)
        ? prev.filter(c => c !== subtypeCode)
        : [...prev, subtypeCode]
    );
  };

  // Toggle profession selection
  const toggleProfession = (professionId) => {
    setSelectedProfessions(prev =>
      prev.includes(professionId)
        ? prev.filter(id => id !== professionId)
        : [...prev, professionId]
    );
  };

  // Close all dropdowns helper
  const closeAllDropdowns = () => {
    setShowJobTypeDropdown(false);
    setShowCountryDropdown(false);
    setShowProfessionDropdown(false);
    setShowDatePostedDropdown(false);
    setShowSalaryDropdown(false);
  };

  // Get currency symbol for salary filter
  const getCurrencySymbol = () => {
    return COUNTRIES[selectedCountry]?.currencySymbol || 'RM';
  };

  // Get salary unit label based on job type
  const getSalaryUnitLabel = () => {
    // Locum (id: 0) is hourly, others are monthly
    if (selectedJobType === null || selectedJobType === 0) {
      return '/hour';
    }
    return '/month';
  };

  // Calculate job details
  const isFullTime = isFullTimeJob(jobDetail?.type);
  const displayCurrency = getJobDisplayCurrency(jobDetail, userCountry || currencyPreset);
  const diffHours = Math.abs(moment(jobDetail?.start_timestamp).diff(moment(jobDetail?.end_timestamp), 'hours') || 1);
  const isFavorited = selectedJob && favorites.includes(selectedJob.uuid);

  const activeFilterCount = [
    selectedJobType !== null,
    selectedCountry !== (userCountry || 'MY'),
    selectedProfessions.length > 0,
    selectedDatePosted !== null,
    !!(shiftDateFrom || shiftDateTo),
    !!(salaryMin || salaryMax),
  ].filter(Boolean).length;

  const filterControls = (
    <>
      {/* Job Type Filter Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            selectedJobType !== null && sharedStyles.filterPillActive,
            showJobTypeDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            setShowJobTypeDropdown(!showJobTypeDropdown);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
          }}
        >
          <Briefcase size={16} color={selectedJobType !== null ? webColors.primary : webColors.textMuted} />
          <Text style={[
            sharedStyles.filterPillLabel,
            selectedJobType !== null && sharedStyles.filterPillLabelActive,
          ]}>
            {selectedJobType !== null
              ? JOB_TYPE_OPTIONS.find(t => t.id === selectedJobType)?.label
              : 'Job Type'}
          </Text>
          <ChevronDown
            size={14}
            color={selectedJobType !== null ? webColors.primary : webColors.textMuted}
            style={showJobTypeDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Job Type Dropdown Menu */}
        {showJobTypeDropdown && (
          <>
            {!isMobile && (
              <Pressable
                style={sharedStyles.dropdownBackdrop}
                onPress={() => setShowJobTypeDropdown(false)}
              />
            )}
            <View style={[sharedStyles.filterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              <View style={sharedStyles.dropdownHeader}>
                <Text style={sharedStyles.dropdownTitle}>Select Job Type</Text>
              </View>
              <View style={sharedStyles.dropdownOptions}>
                {/* All Jobs option */}
                <TouchableOpacity
                  style={[
                    sharedStyles.dropdownOption,
                    selectedJobType === null && sharedStyles.dropdownOptionSelected,
                  ]}
                  onPress={() => {
                    setSelectedJobType(null);
                    setShowJobTypeDropdown(false);
                  }}
                >
                  <View style={[
                    sharedStyles.dropdownRadio,
                    selectedJobType === null && sharedStyles.dropdownRadioSelected,
                  ]}>
                    {selectedJobType === null && <View style={sharedStyles.dropdownRadioDot} />}
                  </View>
                  <Text style={[
                    sharedStyles.dropdownOptionText,
                    selectedJobType === null && sharedStyles.dropdownOptionTextSelected,
                  ]}>All Job Types</Text>
                </TouchableOpacity>
                {JOB_TYPE_OPTIONS.map(type => (
                  <TouchableOpacity
                    key={type.id}
                    style={[
                      sharedStyles.dropdownOption,
                      selectedJobType === type.id && sharedStyles.dropdownOptionSelected,
                    ]}
                    onPress={() => {
                      setSelectedJobType(type.id);
                      setShowJobTypeDropdown(false);
                    }}
                  >
                    <View style={[
                      sharedStyles.dropdownRadio,
                      selectedJobType === type.id && sharedStyles.dropdownRadioSelected,
                    ]}>
                      {selectedJobType === type.id && <View style={sharedStyles.dropdownRadioDot} />}
                    </View>
                    <Text style={[
                      sharedStyles.dropdownOptionText,
                      selectedJobType === type.id && sharedStyles.dropdownOptionTextSelected,
                    ]}>{type.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </>
        )}
      </View>

      {/* Country Filter Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            sharedStyles.filterPillActive,
            showCountryDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            setShowCountryDropdown(!showCountryDropdown);
            setShowJobTypeDropdown(false);
            setShowProfessionDropdown(false);
          }}
        >
          <Globe size={16} color={webColors.primary} />
          <Text style={[sharedStyles.filterPillLabel, sharedStyles.filterPillLabelActive]}>
            {selectedCountry
              ? `${COUNTRIES[selectedCountry]?.flag} ${COUNTRIES[selectedCountry]?.name}`
              : 'Country'}
          </Text>
          <ChevronDown
            size={14}
            color={webColors.primary}
            style={showCountryDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Country Dropdown Menu */}
        {showCountryDropdown && (
          <>
            {!isMobile && (
              <Pressable
                style={sharedStyles.dropdownBackdrop}
                onPress={() => setShowCountryDropdown(false)}
              />
            )}
            <View style={[sharedStyles.filterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              <View style={sharedStyles.dropdownHeader}>
                <Text style={sharedStyles.dropdownTitle}>Select Country</Text>
              </View>
              <View style={sharedStyles.dropdownOptions}>
                {COUNTRY_OPTIONS.map(country => (
                  <TouchableOpacity
                    key={country.code}
                    style={[
                      sharedStyles.dropdownOption,
                      selectedCountry === country.code && sharedStyles.dropdownOptionSelected,
                    ]}
                    onPress={() => {
                      setSelectedCountry(country.code);
                      setShowCountryDropdown(false);
                    }}
                  >
                    <View style={[
                      sharedStyles.dropdownRadio,
                      selectedCountry === country.code && sharedStyles.dropdownRadioSelected,
                    ]}>
                      {selectedCountry === country.code && <View style={sharedStyles.dropdownRadioDot} />}
                    </View>
                    <Text style={[
                      sharedStyles.dropdownOptionText,
                      selectedCountry === country.code && sharedStyles.dropdownOptionTextSelected,
                    ]}>
                      {country.flag} {country.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </>
        )}
      </View>

      {/* Profession Filter Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            selectedProfessions.length > 0 && sharedStyles.filterPillActive,
            showProfessionDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            setShowProfessionDropdown(!showProfessionDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
          }}
        >
          <Stethoscope size={16} color={selectedProfessions.length > 0 ? webColors.primary : webColors.textMuted} />
          <Text style={[
            sharedStyles.filterPillLabel,
            selectedProfessions.length > 0 && sharedStyles.filterPillLabelActive,
          ]}>
            Profession
          </Text>
          {selectedProfessions.length > 0 && (
            <View style={sharedStyles.filterPillCount}>
              <Text style={sharedStyles.filterPillCountText}>{selectedProfessions.length}</Text>
            </View>
          )}
          <ChevronDown
            size={14}
            color={selectedProfessions.length > 0 ? webColors.primary : webColors.textMuted}
            style={showProfessionDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Profession Dropdown Menu */}
        {showProfessionDropdown && (
          <>
            {!isMobile && (
              <Pressable
                style={sharedStyles.dropdownBackdrop}
                onPress={() => setShowProfessionDropdown(false)}
              />
            )}
            <View style={[sharedStyles.filterDropdown, sharedStyles.professionDropdown, isMobile && sharedStyles.filterDropdownMobile, isMobile && sharedStyles.professionDropdownMobile]}>
              <View style={sharedStyles.dropdownHeader}>
                <Text style={sharedStyles.dropdownTitle}>Select Profession & Specialty</Text>
              </View>

              {/* Search Input */}
              <View style={sharedStyles.dropdownSearch}>
                <Search size={16} color={webColors.textMuted} />
                <TextInput
                  style={sharedStyles.dropdownSearchInput}
                  placeholder="Search professions..."
                  placeholderTextColor={webColors.textMuted}
                  value={professionSearchQuery}
                  onChangeText={setProfessionSearchQuery}
                />
                {professionSearchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setProfessionSearchQuery('')}>
                    <X size={14} color={webColors.textMuted} />
                  </TouchableOpacity>
                )}
              </View>

              {/* Profession Options */}
              <ScrollView style={sharedStyles.professionDropdownOptions} showsVerticalScrollIndicator={false}>
                {(professionTypesLoading || professionsLoading) ? (
                  <View style={sharedStyles.professionLoadingContainer}>
                    <ActivityIndicator size="small" color={webColors.primary} />
                    <Text style={sharedStyles.professionLoadingText}>Loading professions...</Text>
                  </View>
                ) : Object.keys(filteredProfessions).length === 0 ? (
                  <View style={sharedStyles.professionEmptyContainer}>
                    <Text style={sharedStyles.professionEmptyText}>No professions found</Text>
                  </View>
                ) : (
                  Object.entries(filteredProfessions).map(([typeCode, typeData]) => {
                    const isExpanded = expandedProfessionTypes.includes(typeCode) || professionSearchQuery.length > 0;
                    const iconConfig = professionTypeIcons[typeCode] || professionTypeIcons.default;
                    const hasSubtypes = typeData.subtypes && typeData.subtypes.length > 0;
                    const allProfessionsInType = [
                      ...typeData.professions,
                      ...(typeData.subtypes || []).flatMap(s => s.professions),
                    ];
                    const selectedInType = allProfessionsInType.filter(p => selectedProfessionsSet.has(p.id)).length;

                    return (
                      <View key={typeCode} style={sharedStyles.professionTypeGroup}>
                        {/* Level 1: Profession Type Header */}
                        <TouchableOpacity
                          style={[
                            sharedStyles.professionTypeHeader,
                            isExpanded && sharedStyles.professionTypeHeaderExpanded,
                          ]}
                          onPress={() => toggleProfessionType(typeCode)}
                        >
                          <Text style={sharedStyles.professionTypeIcon}>{iconConfig.icon}</Text>
                          <Text style={[
                            sharedStyles.professionTypeLabel,
                            isExpanded && sharedStyles.professionTypeLabelExpanded,
                          ]}>
                            {getLocalizedProfessionTypeName(typeData, selectedCountry)}
                          </Text>
                          {selectedInType > 0 && (
                            <View style={[sharedStyles.professionTypeCount, { backgroundColor: iconConfig.bgColor }]}>
                              <Text style={[sharedStyles.professionTypeCountText, { color: iconConfig.color }]}>
                                {selectedInType}
                              </Text>
                            </View>
                          )}
                          <ChevronRight
                            size={16}
                            color={isExpanded ? webColors.primary : webColors.textMuted}
                            style={{ transform: [{ rotate: isExpanded ? '90deg' : '0deg' }] }}
                          />
                        </TouchableOpacity>

                        {/* Expanded content */}
                        {isExpanded && (
                          <View style={sharedStyles.professionItemsList}>
                            {/* Level 2: Sub-types (if any) */}
                            {hasSubtypes && typeData.subtypes.map(subType => {
                              const isSubExpanded = expandedSubtypes.includes(subType.code) || professionSearchQuery.length > 0;
                              const selectedInSub = subType.professions.filter(p => selectedProfessionsSet.has(p.id)).length;

                              return (
                                <View key={subType.code} style={sharedStyles.professionSubtypeGroup}>
                                  <TouchableOpacity
                                    style={[
                                      sharedStyles.professionSubtypeHeader,
                                      isSubExpanded && sharedStyles.professionSubtypeHeaderExpanded,
                                    ]}
                                    onPress={() => toggleSubtype(subType.code)}
                                  >
                                    <Text style={[
                                      sharedStyles.professionSubtypeLabel,
                                      isSubExpanded && sharedStyles.professionSubtypeLabelExpanded,
                                    ]}>
                                      {getLocalizedProfessionTypeName(subType, selectedCountry)}
                                    </Text>
                                    {selectedInSub > 0 && (
                                      <View style={[sharedStyles.professionSubtypeCount, { backgroundColor: iconConfig.bgColor }]}>
                                        <Text style={[sharedStyles.professionSubtypeCountText, { color: iconConfig.color }]}>
                                          {selectedInSub}
                                        </Text>
                                      </View>
                                    )}
                                    <ChevronRight
                                      size={14}
                                      color={isSubExpanded ? webColors.primary : webColors.textMuted}
                                      style={{ transform: [{ rotate: isSubExpanded ? '90deg' : '0deg' }] }}
                                    />
                                  </TouchableOpacity>

                                  {/* Level 3: Professions under sub-type */}
                                  {isSubExpanded && (
                                    <View style={sharedStyles.professionSubtypeItemsList}>
                                      {subType.professions.map(profession => {
                                        const isSelected = selectedProfessionsSet.has(profession.id);
                                        const professionName = getLocalizedProfessionName(profession, selectedCountry);
                                        if (professionName.length === 0) return null;
                                        return (
                                          <TouchableOpacity
                                            key={profession.id}
                                            style={[
                                              sharedStyles.professionItem,
                                              isSelected && sharedStyles.professionItemSelected,
                                            ]}
                                            onPress={() => toggleProfession(profession.id)}
                                          >
                                            <View style={[
                                              sharedStyles.professionCheckbox,
                                              isSelected && sharedStyles.professionCheckboxSelected,
                                            ]}>
                                              {isSelected && <Check size={12} color="#fff" />}
                                            </View>
                                            <Text style={[
                                              sharedStyles.professionItemText,
                                              isSelected && sharedStyles.professionItemTextSelected,
                                            ]}>
                                              {professionName}
                                            </Text>
                                          </TouchableOpacity>
                                        );
                                      })}
                                    </View>
                                  )}
                                </View>
                              );
                            })}

                            {/* Direct professions (for types without subtypes) */}
                            {typeData.professions.map(profession => {
                              const isSelected = selectedProfessionsSet.has(profession.id);
                              const professionName = getLocalizedProfessionName(profession, selectedCountry);
                              if (professionName.length === 0) return null;
                              return (
                                <TouchableOpacity
                                  key={profession.id}
                                  style={[
                                    sharedStyles.professionItem,
                                    isSelected && sharedStyles.professionItemSelected,
                                  ]}
                                  onPress={() => toggleProfession(profession.id)}
                                >
                                  <View style={[
                                    sharedStyles.professionCheckbox,
                                    isSelected && sharedStyles.professionCheckboxSelected,
                                  ]}>
                                    {isSelected && <Check size={12} color="#fff" />}
                                  </View>
                                  <Text style={[
                                    sharedStyles.professionItemText,
                                    isSelected && sharedStyles.professionItemTextSelected,
                                  ]}>
                                    {professionName}
                                  </Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        )}
                      </View>
                    );
                  })
                )}
              </ScrollView>

              {/* Clear Selection */}
              {selectedProfessions.length > 0 && (
                <View style={sharedStyles.professionDropdownFooter}>
                  <TouchableOpacity
                    style={sharedStyles.professionClearBtn}
                    onPress={() => setSelectedProfessions([])}
                  >
                    <Text style={sharedStyles.professionClearBtnText}>Clear All ({selectedProfessions.length})</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </>
        )}
      </View>

      {/* Date Filter Dropdown (Tabbed: Posted Date + Shift Date) */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            (selectedDatePosted !== null || shiftDateFrom || shiftDateTo) && sharedStyles.filterPillActive,
            showDatePostedDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            if (!showDatePostedDropdown) {
              // Initialize temp values from actual values when opening
              setTempShiftDateFrom(shiftDateFrom);
              setTempShiftDateTo(shiftDateTo);
              setSelectedQuickRange(null);
            }
            setShowDatePostedDropdown(!showDatePostedDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
            setShowSalaryDropdown(false);
          }}
        >
          <Calendar size={16} color={(selectedDatePosted !== null || shiftDateFrom || shiftDateTo) ? webColors.primary : webColors.textMuted} />
          <Text style={[
            sharedStyles.filterPillLabel,
            (selectedDatePosted !== null || shiftDateFrom || shiftDateTo) && sharedStyles.filterPillLabelActive,
          ]}>
            {selectedDatePosted
              ? DATE_POSTED_OPTIONS.find(opt => opt.value === selectedDatePosted)?.label
              : shiftDateFrom || shiftDateTo
                ? `Shift: ${shiftDateFrom && moment(shiftDateFrom, 'YYYY-MM-DD', true).isValid() ? moment(shiftDateFrom).format('MMM D') : 'Start'} - ${shiftDateTo && moment(shiftDateTo, 'YYYY-MM-DD', true).isValid() ? moment(shiftDateTo).format('MMM D') : 'End'}`
                : 'Date'}
          </Text>
          <ChevronDown
            size={14}
            color={(selectedDatePosted !== null || shiftDateFrom || shiftDateTo) ? webColors.primary : webColors.textMuted}
            style={showDatePostedDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Date Filter Dropdown Menu */}
        {showDatePostedDropdown && (
          <>
            {!isMobile && (
              <Pressable
                style={sharedStyles.dropdownBackdrop}
                onPress={() => setShowDatePostedDropdown(false)}
              />
            )}
            <View style={[sharedStyles.filterDropdown, sharedStyles.dateFilterDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              {/* Underline Tabs */}
              <View style={sharedStyles.dateFilterTabs}>
                <Pressable
                  style={[sharedStyles.dateFilterTab, currentDateTab === 'posted' && sharedStyles.dateFilterTabActive]}
                  onPress={() => setCurrentDateTab('posted')}
                >
                  <Calendar size={14} color={currentDateTab === 'posted' ? webColors.primary : webColors.textMuted} />
                  <Text style={[sharedStyles.dateFilterTabText, currentDateTab === 'posted' && sharedStyles.dateFilterTabTextActive]}>
                    Posted Date
                  </Text>
                </Pressable>
                <Pressable
                  style={[sharedStyles.dateFilterTab, currentDateTab === 'shift' && sharedStyles.dateFilterTabActive]}
                  onPress={() => setCurrentDateTab('shift')}
                >
                  <CalendarDays size={14} color={currentDateTab === 'shift' ? webColors.primary : webColors.textMuted} />
                  <Text style={[sharedStyles.dateFilterTabText, currentDateTab === 'shift' && sharedStyles.dateFilterTabTextActive]}>
                    Shift Date
                  </Text>
                  <View style={[sharedStyles.dateFilterTabBadge, currentDateTab === 'shift' && sharedStyles.dateFilterTabBadgeActive]}>
                    <Text style={[sharedStyles.dateFilterTabBadgeText, currentDateTab === 'shift' && sharedStyles.dateFilterTabBadgeTextActive]}>
                      Locum
                    </Text>
                  </View>
                </Pressable>
              </View>

              {/* Posted Date Tab Content */}
              {currentDateTab === 'posted' && (
                <View style={sharedStyles.dateFilterTabContent}>
                  <View style={sharedStyles.dateFilterOptions}>
                    {DATE_POSTED_OPTIONS.map((option) => (
                      <Pressable
                        key={option.value}
                        style={[sharedStyles.dateFilterOption, selectedDatePosted === option.value && sharedStyles.dateFilterOptionSelected]}
                        onPress={() => {
                          setSelectedDatePosted(option.value);
                          // Clear shift date when selecting posted date
                          setShiftDateFrom('');
                          setShiftDateTo('');
                          setTempShiftDateFrom('');
                          setTempShiftDateTo('');
                          setSelectedQuickRange(null);
                          // Close dropdown immediately for better UX
                          setShowDatePostedDropdown(false);
                        }}
                      >
                        <View style={[sharedStyles.dateFilterRadio, selectedDatePosted === option.value && sharedStyles.dateFilterRadioSelected]} />
                        <Text style={sharedStyles.dateFilterOptionText}>{option.label}</Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}

              {/* Shift Date Tab Content */}
              {currentDateTab === 'shift' && (
                <View style={sharedStyles.dateFilterTabContent}>
                  <View style={sharedStyles.shiftDateContent}>
                    {/* Info Note */}
                    <View style={sharedStyles.shiftDateNote}>
                      <Lightbulb size={14} color="#92400e" />
                      <Text style={sharedStyles.shiftDateNoteText}>
                        Find Locum shifts available on specific dates
                      </Text>
                    </View>

                    {/* Date Inputs */}
                    <View style={sharedStyles.shiftDateRow}>
                      <View style={sharedStyles.shiftDateInputGroup}>
                        <Text style={sharedStyles.shiftDateLabel}>From</Text>
                        <TextInput
                          style={[sharedStyles.shiftDateInput, tempShiftDateFrom && sharedStyles.shiftDateInputActive]}
                          placeholder="DD-MM-YYYY"
                          placeholderTextColor={webColors.textMuted}
                          value={tempShiftDateFrom}
                          onChangeText={(val) => {
                            setTempShiftDateFrom(val);
                            setSelectedQuickRange(null);
                          }}
                          onFocus={(e) => { e.target.type = 'date'; }}
                          onBlur={(e) => { if (!tempShiftDateFrom) e.target.type = 'text'; }}
                        />
                      </View>
                      <View style={sharedStyles.shiftDateInputGroup}>
                        <Text style={sharedStyles.shiftDateLabel}>To</Text>
                        <TextInput
                          style={[sharedStyles.shiftDateInput, tempShiftDateTo && sharedStyles.shiftDateInputActive]}
                          placeholder="DD-MM-YYYY"
                          placeholderTextColor={webColors.textMuted}
                          value={tempShiftDateTo}
                          onChangeText={(val) => {
                            setTempShiftDateTo(val);
                            setSelectedQuickRange(null);
                          }}
                          onFocus={(e) => { e.target.type = 'date'; }}
                          onBlur={(e) => { if (!tempShiftDateTo) e.target.type = 'text'; }}
                        />
                      </View>
                    </View>

                    {/* Quick Select */}
                    <Text style={sharedStyles.quickSelectLabel}>Quick Select</Text>
                    <View style={sharedStyles.quickSelectBtns}>
                      {QUICK_RANGE_OPTIONS.map((option) => (
                        <Pressable
                          key={option.value}
                          style={[sharedStyles.quickSelectBtn, selectedQuickRange === option.value && sharedStyles.quickSelectBtnActive]}
                          onPress={() => handleQuickRangeSelect(option.value)}
                        >
                          <Text style={[sharedStyles.quickSelectBtnText, selectedQuickRange === option.value && sharedStyles.quickSelectBtnTextActive]}>
                            {option.label}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                </View>
              )}

              {/* Footer */}
              <View style={sharedStyles.dateFilterFooter}>
                <Pressable
                  style={sharedStyles.dateFilterClearBtn}
                  onPress={() => {
                    setSelectedDatePosted(null);
                    setShiftDateFrom('');
                    setShiftDateTo('');
                    setTempShiftDateFrom('');
                    setTempShiftDateTo('');
                    setSelectedQuickRange(null);
                    setShowDatePostedDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.dateFilterClearBtnText}>Clear</Text>
                </Pressable>
                <Pressable
                  style={sharedStyles.dateFilterApplyBtn}
                  onPress={() => {
                    if (currentDateTab === 'shift') {
                      setShiftDateFrom(tempShiftDateFrom);
                      setShiftDateTo(tempShiftDateTo);
                      if (tempShiftDateFrom || tempShiftDateTo) {
                        setSelectedDatePosted(null);
                      }
                    }
                    setShowDatePostedDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.dateFilterApplyBtnText}>Apply</Text>
                </Pressable>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Salary Filter Dropdown */}
      <View style={sharedStyles.filterDropdownWrapper}>
        <TouchableOpacity
          style={[
            sharedStyles.filterPill,
            (salaryMin || salaryMax) && sharedStyles.filterPillActive,
            showSalaryDropdown && sharedStyles.filterPillOpen,
            isMobile && sharedStyles.filterPillMobile,
          ]}
          onPress={() => {
            if (!showSalaryDropdown) {
              // Initialize temp values from actual values when opening
              setTempSalaryMin(salaryMin);
              setTempSalaryMax(salaryMax);
            }
            setShowSalaryDropdown(!showSalaryDropdown);
            setShowJobTypeDropdown(false);
            setShowCountryDropdown(false);
            setShowProfessionDropdown(false);
            setShowDatePostedDropdown(false);
          }}
        >
          <DollarSign size={16} color={(salaryMin || salaryMax) ? webColors.primary : webColors.textSecondary} />
          <Text style={[
            sharedStyles.filterPillLabel,
            (salaryMin || salaryMax) && sharedStyles.filterPillLabelActive,
          ]}>
            {(salaryMin || salaryMax)
              ? `${getCurrencySymbol()} ${salaryMin || '0'} - ${salaryMax || 'Any'}`
              : 'Salary'}
          </Text>
          <ChevronDown
            size={14}
            color={(salaryMin || salaryMax) ? webColors.primary : webColors.textMuted}
            style={showSalaryDropdown ? { transform: [{ rotate: '180deg' }] } : {}}
          />
        </TouchableOpacity>

        {/* Salary Dropdown Menu */}
        {showSalaryDropdown && (
          <>
            {!isMobile && (
              <Pressable
                style={sharedStyles.dropdownBackdrop}
                onPress={() => setShowSalaryDropdown(false)}
              />
            )}
            <View style={[sharedStyles.filterDropdown, sharedStyles.salaryDropdown, isMobile && sharedStyles.filterDropdownMobile]}>
              <View style={sharedStyles.dropdownHeader}>
                <Text style={sharedStyles.dropdownTitle}>Salary Range</Text>
              </View>
              <View style={sharedStyles.salaryDropdownContent}>
                <View style={sharedStyles.salaryInputsRow}>
                  <View style={sharedStyles.salaryInputWrapper}>
                    <Text style={sharedStyles.salaryInputLabel}>Minimum</Text>
                    <View style={sharedStyles.salaryInputContainer}>
                      <Text style={sharedStyles.salaryInputPrefix}>{getCurrencySymbol()}</Text>
                      <TextInput
                        style={sharedStyles.salaryInput}
                        placeholder="0"
                        placeholderTextColor={webColors.textMuted}
                        value={tempSalaryMin}
                        onChangeText={(val) => setTempSalaryMin(val.replace(/[^0-9]/g, ''))}
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                  <Text style={sharedStyles.salaryDivider}>—</Text>
                  <View style={sharedStyles.salaryInputWrapper}>
                    <Text style={sharedStyles.salaryInputLabel}>Maximum</Text>
                    <View style={sharedStyles.salaryInputContainer}>
                      <Text style={sharedStyles.salaryInputPrefix}>{getCurrencySymbol()}</Text>
                      <TextInput
                        style={sharedStyles.salaryInput}
                        placeholder="Any"
                        placeholderTextColor={webColors.textMuted}
                        value={tempSalaryMax}
                        onChangeText={(val) => setTempSalaryMax(val.replace(/[^0-9]/g, ''))}
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                </View>
              </View>
              <View style={sharedStyles.salaryDropdownFooter}>
                <TouchableOpacity
                  style={sharedStyles.salaryClearBtn}
                  onPress={() => {
                    setTempSalaryMin('');
                    setTempSalaryMax('');
                    setSalaryMin('');
                    setSalaryMax('');
                    setShowSalaryDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.salaryClearBtnText}>Clear</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={sharedStyles.salaryApplyBtn}
                  onPress={() => {
                    setSalaryMin(tempSalaryMin);
                    setSalaryMax(tempSalaryMax);
                    setShowSalaryDropdown(false);
                  }}
                >
                  <Text style={sharedStyles.salaryApplyBtnText}>Apply</Text>
                </TouchableOpacity>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Clear Filters */}
      {(searchQuery || locationSearchQuery || selectedJobType !== null || selectedCountry !== (userCountry || 'MY') || selectedProfessions.length > 0 || selectedDatePosted !== null || shiftDateFrom || shiftDateTo || salaryMin || salaryMax) && (
        <Pressable
          style={sharedStyles.clearFiltersInline}
          onPress={() => {
            setSearchQuery('');
            setLocationSearchQuery('');
            setSelectedJobType(null);
            setSelectedCountry(userCountry || 'MY');
            setSelectedProfessions([]);
            setSelectedDatePosted(null);
            setShiftDateFrom('');
            setShiftDateTo('');
            setTempShiftDateFrom('');
            setTempShiftDateTo('');
            setSelectedQuickRange(null);
            setSalaryMin('');
            setSalaryMax('');
            setTempSalaryMin('');
            setTempSalaryMax('');
          }}
        >
          <X size={14} color={webColors.textMuted} />
          <Text style={sharedStyles.clearFiltersInlineText}>Clear</Text>
        </Pressable>
      )}
    </>
  );

  return (
    <WebLayout>
      <View style={sharedStyles.pageHeaderContainer}>
        {/* Header */}
        <View style={sharedStyles.pageHeader}>
          <Text style={sharedStyles.pageTitle}>Explore Healthcare jobs, Find your Fit</Text>
          <Text style={sharedStyles.pageSubtitle}>Welcome to the leading platform for medical professionals. Whether you're a doctor, nurse, pharmacist, dentist, physiotherapist, or allied health professional — explore full-time, part-time, and locum and International job opportunities that match your skills, schedule, and salary goals</Text>
        </View>

        {/* All Jobs / Saved Jobs Tabs */}
        <View style={viewTabsStyles.viewTabs}>
          <TouchableOpacity
            style={[viewTabsStyles.viewTab, activeTab === 'all' && viewTabsStyles.viewTabActive]}
            onPress={() => setActiveTab('all')}
          >
            <Briefcase size={16} color={activeTab === 'all' ? '#ffffff' : webColors.textMuted} />
            <Text style={[viewTabsStyles.viewTabText, activeTab === 'all' && viewTabsStyles.viewTabTextActive]}>
              All Jobs
            </Text>
            {jobs.length > 0 && (
              <View style={[viewTabsStyles.viewTabBadge, activeTab === 'all' && viewTabsStyles.viewTabBadgeActive]}>
                <Text style={[viewTabsStyles.viewTabBadgeText, activeTab === 'all' && viewTabsStyles.viewTabBadgeTextActive]}>
                  {jobs.length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[viewTabsStyles.viewTab, activeTab === 'saved' && viewTabsStyles.viewTabActive]}
            onPress={() => setActiveTab('saved')}
          >
            <Heart size={16} color={activeTab === 'saved' ? '#ffffff' : webColors.textMuted} />
            <Text style={[viewTabsStyles.viewTabText, activeTab === 'saved' && viewTabsStyles.viewTabTextActive]}>
              Saved Jobs
            </Text>
            {savedJobsCount > 0 && (
              <View style={[viewTabsStyles.viewTabBadge, activeTab === 'saved' && viewTabsStyles.viewTabBadgeActive]}>
                <Text style={[viewTabsStyles.viewTabBadgeText, activeTab === 'saved' && viewTabsStyles.viewTabBadgeTextActive]}>
                  {savedJobsCount}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>

        {/* Search and Filters - Only show for All Jobs tab */}
        {activeTab === 'all' && (
          <>
            <View style={sharedStyles.searchSection}>
              <View style={[sharedStyles.searchRow, isMobile && sharedStyles.searchRowMobile]}>
                {/* Job Search Section */}
                <View style={[sharedStyles.searchSectionJob, isMobile && sharedStyles.searchSectionJobMobile]}>
                  <View style={sharedStyles.searchIcon}>
                    <Search size={20} color={webColors.textMuted} />
                  </View>
                  <TextInput
                    style={sharedStyles.searchInput}
                    placeholder="Job title, keywords, or company"
                    placeholderTextColor={webColors.textMuted}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                  />
                  {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setSearchQuery('')} style={sharedStyles.clearBtn}>
                      <X size={14} color={webColors.textMuted} />
                    </TouchableOpacity>
                  )}
                </View>
                {/* Location Search Section */}
                <View style={[sharedStyles.searchSectionLocation, isMobile && sharedStyles.searchSectionLocationMobile]}>
                  <View style={sharedStyles.searchIcon}>
                    <MapPin size={20} color={webColors.textMuted} />
                  </View>
                  <TextInput
                    style={sharedStyles.searchInput}
                    placeholder="City, state or town"
                    placeholderTextColor={webColors.textMuted}
                    value={locationSearchQuery}
                    onChangeText={setLocationSearchQuery}
                  />
                  {locationSearchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => setLocationSearchQuery('')} style={sharedStyles.clearBtn}>
                      <X size={14} color={webColors.textMuted} />
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              {isMobile ? (
                <TouchableOpacity
                  style={[sharedStyles.filtersButton, activeFilterCount > 0 && sharedStyles.filtersButtonActive]}
                  onPress={() => setShowFiltersSheet(true)}
                >
                  <Filter size={16} color={activeFilterCount > 0 ? webColors.primary : webColors.textSecondary} />
                  <Text style={sharedStyles.filtersButtonText}>
                    Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                  </Text>
                </TouchableOpacity>
              ) : (
                <View style={sharedStyles.filterPillsRow}>
                  {filterControls}
                </View>
              )}
            </View>
            {isMobile && showFiltersSheet && (
              <>
                <Pressable style={sharedStyles.filtersSheetOverlay} onPress={() => setShowFiltersSheet(false)} />
                <View style={sharedStyles.filtersSheet}>
                  <View style={sharedStyles.filtersSheetHeader}>
                    <Text style={sharedStyles.filtersSheetTitle}>Filters</Text>
                    <TouchableOpacity onPress={() => setShowFiltersSheet(false)}>
                      <X size={22} color={webColors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                  <ScrollView style={sharedStyles.filtersSheetBody} contentContainerStyle={{ gap: 12 }}>
                    <View style={[sharedStyles.filterPillsRow, sharedStyles.filterPillsRowMobile]}>
                      {filterControls}
                    </View>
                  </ScrollView>
                  <View style={sharedStyles.filtersSheetFooter}>
                    <TouchableOpacity
                      style={sharedStyles.filtersSheetApplyBtn}
                      onPress={() => setShowFiltersSheet(false)}
                    >
                      <Text style={sharedStyles.filtersSheetApplyBtnText}>
                        Show {displayedJobs.length} result{displayedJobs.length !== 1 ? 's' : ''}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </>
            )}
          </>
        )}
      </View>

      {/* Results Count */}
      {!isLoading && displayedJobs.length > 0 && (
        <View style={sharedStyles.resultsInfo}>
          <Text style={sharedStyles.resultsCount}>
            {displayedJobs.length} {activeTab === 'saved' ? 'saved ' : ''}job{displayedJobs.length !== 1 ? 's' : ''}{activeTab === 'all' ? ' found' : ''}
          </Text>
        </View>
      )}

      {/* Main Content - Master-Detail Layout */}
      <View style={[sharedStyles.masterDetailLayout, isMobile && sharedStyles.masterDetailLayoutMobile]}>
        {/* Left Panel - Job List */}
        {(!isMobile || mobileView === 'list') && (
        <View style={[sharedStyles.jobListPanel, isMobile && sharedStyles.jobListPanelMobile]}>
            {isLoading ? (
              <View style={sharedStyles.loadingContainer}>
                <ActivityIndicator size="large" color={webColors.primary} />
                <Text style={sharedStyles.loadingText}>Loading jobs...</Text>
              </View>
            ) : error ? (
              <View style={sharedStyles.errorState}>
                <View style={sharedStyles.errorIcon}>
                  <AlertTriangle size={48} color="#f59e0b" />
                </View>
                <Text style={sharedStyles.errorTitle}>Unable to load jobs</Text>
                <Text style={sharedStyles.errorText}>Please check your connection and try again</Text>
              </View>
            ) : paginatedJobs.length > 0 ? (
              <>
                <View style={sharedStyles.jobCardsList}>
                  {paginatedJobs.map((job) => (
                    <JobListCard
                      key={job.uuid || job.id}
                      job={job}
                      isSelected={selectedJob?.uuid === job.uuid}
                      isSaved={favorites.includes(job.uuid)}
                      onPress={() => {
                        setSelectedJob(job);
                        if (isMobile) setMobileView('detail');
                      }}
                    />
                  ))}
                </View>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={displayedJobs.length}
                    pageSize={JOBS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            ) : activeTab === 'saved' ? (
              <View style={sharedStyles.emptyState}>
                <View style={sharedStyles.emptyIcon}>
                  <Heart size={48} color="#9ca3af" />
                </View>
                <Text style={sharedStyles.emptyTitle}>No saved jobs yet</Text>
                <Text style={sharedStyles.emptyText}>Jobs you save will appear here</Text>
                <TouchableOpacity
                  style={sharedStyles.clearFiltersBtn}
                  onPress={() => setActiveTab('all')}
                >
                  <Text style={sharedStyles.clearFiltersBtnText}>Browse Jobs</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={sharedStyles.emptyState}>
                <View style={sharedStyles.emptyIcon}>
                  <ClipboardList size={48} color="#9ca3af" />
                </View>
                <Text style={sharedStyles.emptyTitle}>No jobs found</Text>
                <Text style={sharedStyles.emptyText}>Try adjusting your search or filters</Text>
                {(searchQuery || locationSearchQuery || selectedJobType !== null || selectedCountry !== (userCountry || 'MY') || selectedProfessions.length > 0 || selectedDatePosted !== null || shiftDateFrom || shiftDateTo || salaryMin || salaryMax) && (
                  <TouchableOpacity
                    style={sharedStyles.clearFiltersBtn}
                    onPress={() => {
                      setSearchQuery('');
                      setLocationSearchQuery('');
                      setSelectedJobType(null);
                      setSelectedCountry(userCountry || 'MY');
                      setSelectedProfessions([]);
                      setSelectedDatePosted(null);
                      setShiftDateFrom('');
                      setShiftDateTo('');
                      setTempShiftDateFrom('');
                      setTempShiftDateTo('');
                      setSelectedQuickRange(null);
                      setSalaryMin('');
                      setSalaryMax('');
                    }}
                  >
                    <Text style={sharedStyles.clearFiltersBtnText}>Clear Filters</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
        </View>
        )}

        {/* Right Panel - Job Detail */}
        {(!isMobile || mobileView === 'detail') && (
        <View style={[sharedStyles.jobDetailPanel, isMobile && sharedStyles.jobDetailPanelMobile]}>
          {isMobile && (
            <MobileDetailBackBar label="Back to results" onPress={() => setMobileView('list')} />
          )}
          <ScrollView style={[sharedStyles.detailScroll, isMobile && sharedStyles.detailScrollMobile]} showsVerticalScrollIndicator={true}>
            {!selectedJob ? (
              <View style={sharedStyles.noSelectionState}>
                <View style={sharedStyles.noSelectionIcon}>
                  <Briefcase size={48} color="#9ca3af" />
                </View>
                <Text style={sharedStyles.noSelectionText}>Select a job to view details</Text>
              </View>
            ) : detailLoading ? (
              <View style={sharedStyles.detailLoading}>
                <ActivityIndicator size="large" color={webColors.primary} />
                <Text style={sharedStyles.loadingText}>Loading details...</Text>
              </View>
            ) : (
              <JobDetailContent
                job={jobDetail}
                jobFacilityData={jobFacilityData}
                isFullTime={isFullTime}
                diffHours={diffHours}
                displayCurrency={displayCurrency}
                isFavorited={isFavorited}
                onApply={() => {
                  if (profileInfo?.is_approved) {
                    setAppliedJobDetail(jobDetail);
                    setShowApplyModal(true);
                  } else {
                    alert('Please complete your profile to apply for jobs.');
                    navigation.navigate('ProfileDoctor');
                  }
                }}
                onToggleFavorite={toggleFavorite}
                shareUrl={isDoctor ? `https://hiresupermedic.com/job/${selectedJob?.uuid}` : `https://employer.hiresupermedic.com/job/${selectedJob?.uuid}`}
              />
            )}
          </ScrollView>
        </View>
        )}
      </View>

      {/* Apply Modal */}
      <WebModal
        visible={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setApplySuccess(false);
          setAppliedJobDetail(null);
        }}
        title={applySuccess ? 'Application Submitted!' : `Apply to ${appliedJobDetail?.title}`}
      >
        {applySuccess ? (
          <View style={modalStyles.successContent}>
            <View style={modalStyles.successIcon}>
              <CheckCircle size={28} color="#16a34a" />
            </View>
            <Text style={modalStyles.successTitle}>Application Submitted!</Text>
            <Text style={modalStyles.successText}>
              Your application has been sent to {appliedJobDetail?.clinic?.name}. You'll be notified when they respond.
            </Text>
            <TouchableOpacity
              style={modalStyles.successBtn}
              onPress={() => {
                setShowApplyModal(false);
                setApplySuccess(false);
              }}
            >
              <Text style={modalStyles.successBtnText}>Continue Browsing</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={modalStyles.applyModalContent}>
            <Text style={modalStyles.applyModalText}>
              Are you sure you want to apply for this job at {appliedJobDetail?.clinic?.name}?
            </Text>
            <View style={modalStyles.applyModalDetails}>
              <View style={modalStyles.applyModalDetailRow}>
                <MapPin size={14} color={webColors.textSecondary} />
                <Text style={modalStyles.applyModalDetail}>{appliedJobDetail?.clinic?.city}</Text>
              </View>
              <View style={modalStyles.applyModalDetailRow}>
                <Text style={modalStyles.applyModalDetail}>{formatPrice(appliedJobDetail?.price, displayCurrency)}</Text>
              </View>
              <View style={modalStyles.applyModalDetailRow}>
                <Calendar size={14} color={webColors.textSecondary} />
                <Text style={modalStyles.applyModalDetail}>{moment(appliedJobDetail?.start_timestamp).format('MMM D, YYYY')}</Text>
              </View>
            </View>
            <View style={modalStyles.applyModalActions}>
              <TouchableOpacity style={modalStyles.cancelModalBtn} onPress={() => setShowApplyModal(false)}>
                <Text style={modalStyles.cancelModalBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[modalStyles.confirmModalBtn, applyMutation.isPending && modalStyles.confirmModalBtnDisabled]}
                onPress={handleApply}
                disabled={applyMutation.isPending}
              >
                <Text style={modalStyles.confirmModalBtnText}>
                  {applyMutation.isPending ? 'Submitting...' : 'Apply Now'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </WebModal>
    </WebLayout>
  );
};

export default JobsScreen;
